// Considered: TypeFieldRowMolecule alone for the whole editor — reused for every labelled field
// (name, offset, key date, assigned-to, requirement prompt/aspect), but the editor also
// carries section headings, static notes, a terminal-stage choice and a requirement-card list that
// TypeFieldRowMolecule has no slot for, so this molecule composes it rather than wrapping it.
// Considered: ArcStageCardMolecule — that renders the read-mostly lane-grid card (index, chips,
// requirement callout); this is the full editing surface the inspector rail opens for a selected
// stage, with every field writable. See spec Reuse Audit (arc-editor-canvas-v1.md).
import React from 'react';
import { Trans, useTranslation } from 'localization';
import type { Button } from 'primereact/button';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import GlyphAtom from '../../atoms/Glyph/GlyphAtom';
import TagAtom from '../../atoms/Tag/TagAtom';
import ChoiceCardGroupAtom from '../../atoms/ChoiceCardGroup/ChoiceCardGroupAtom';
import TypeFieldRowMolecule from '../studio/TypeFieldRowMolecule';
import InlineConfirmMolecule from '../studio/InlineConfirmMolecule';
import { focusPrimeButton } from '../../../utils/focusPrimeButton';
import './ArcStageInspectorMolecule.scss';

export interface ArcStageInspectorSelectOption {
  value: string;
  label: string;
}

export interface ArcStageInspectorRequirement {
  /** Stable identity for `key`/removal — the arc's own requirement index is fine. */
  id: string;
  aspectRef: string;
  aspectOptions: ArcStageInspectorSelectOption[];
  prompt: string;
}

export interface ArcStageInspectorIntent {
  id: string;
  name: string;
  perspectiveLabel: string;
}

export interface ArcStageInspectorMoleculeProps {
  name: string;
  onNameChange: (value: string) => void;
  keyDateOptions: ArcStageInspectorSelectOption[];
  /** `''` — no key date attached. */
  keyDateValue: string;
  onKeyDateChange: (value: string) => void;
  offset: string;
  onOffsetChange: (value: string) => void;
  /** Whole-days-only parse succeeded (`acOffsetSupported`) — `false` shows the engine-parse warning. */
  offsetSupported: boolean;
  /** `when.vacant` — nothing has answered this key date yet, so no date renders anywhere. */
  vacant: boolean;
  /** The unanswered (or answered) key date's own name, read into both notes below. */
  whenNeed: string;
  /** Only meaningful when `vacant` is false — how the resolved date reads. */
  whenText?: string;
  requirements: ArcStageInspectorRequirement[];
  onAddRequirement: () => void;
  onRemoveRequirement: (id: string) => void;
  onRequirementAspectChange: (id: string, value: string) => void;
  onRequirementPromptChange: (id: string, value: string) => void;
  pinName: string;
  /** `acArchetype(arc.archetype).terminalAllowed` — some archetypes never rest (`false`). */
  terminalAllowed: boolean;
  /** Only meaningful when `terminalAllowed` is false. */
  archetypeLabel?: string;
  archetypeNote?: string;
  isTerminal: boolean;
  onIsTerminalChange: (value: boolean) => void;
  assignedTo: string;
  assignedToOptions: ArcStageInspectorSelectOption[];
  onAssignedToChange: (value: string) => void;
  intents: ArcStageInspectorIntent[];
  /** `onEnter` + `onExit` actions the stage carries — named in the remove confirm, since removing
   * the stage removes them with it. */
  actionCount: number;
  /** Transitions from other stages that target this one — they dangle once it is removed, and
   * the remove confirm says so. */
  inboundTransitionCount: number;
  editable: boolean;
  onApply: () => void;
  /** Called only after the inline confirm is accepted. */
  onRemove: () => void;
  className?: string;
}

/** The stage editor the inspector rail's `selection.body` hosts for `selection.kind === 'stage'`
 * (`arc-inspector.jsx:144-261`, `StageEditor`). One instance per selected stage — the rail's own
 * head (kind eyebrow, stage name, close) is `InspectorRailOrganism`'s `selection.kind`/`title`, not
 * this component's concern. */
const ArcStageInspectorMolecule: React.FC<ArcStageInspectorMoleculeProps> = ({
  name,
  onNameChange,
  keyDateOptions,
  keyDateValue,
  onKeyDateChange,
  offset,
  onOffsetChange,
  offsetSupported,
  vacant,
  whenNeed,
  whenText,
  requirements,
  onAddRequirement,
  onRemoveRequirement,
  onRequirementAspectChange,
  onRequirementPromptChange,
  pinName,
  terminalAllowed,
  archetypeLabel,
  archetypeNote,
  isTerminal,
  onIsTerminalChange,
  assignedTo,
  assignedToOptions,
  onAssignedToChange,
  intents,
  actionCount,
  inboundTransitionCount,
  editable,
  onApply,
  onRemove,
  className = '',
}) => {
  const { t } = useTranslation();
  const ns = 'studio.arcEditor.inspector.stage';
  const [nameTouched, setNameTouched] = React.useState(false);
  const nameError = nameTouched && !name.trim() ? t(`${ns}.nameRequired`) : undefined;
  // Both removes are consequential (the spec's reversal table) — the inline confirm states the
  // effect and scope in place; "Keep" returns focus to the button that opened it.
  const [confirmingRemove, setConfirmingRemove] = React.useState(false);
  const [confirmingRequirementId, setConfirmingRequirementId] = React.useState<string | null>(null);
  const removeButtonRef = React.useRef<Button>(null);
  const requirementRemoveRefs = React.useRef<Record<string, Button | null>>({});
  const keepStage = () => {
    setConfirmingRemove(false);
    focusPrimeButton(removeButtonRef.current);
  };
  const keepRequirement = (id: string) => {
    setConfirmingRequirementId(null);
    focusPrimeButton(requirementRemoveRefs.current[id]);
  };

  return (
    <div className={`arc-stage-inspector ${className}`.trim()} data-datum="side.panel#2" data-kind="panel">
      <div className="arc-stage-inspector__body">
        <TypeFieldRowMolecule
          label={t(`${ns}.nameLabel`)}
          layout="stacked"
          mode={editable ? 'edit' : 'disabled'}
          kind="field"
          datum="side.children"
          labelDatum="side.label"
          errorMessage={nameError}
          controls={[
            {
              kind: 'text',
              value: name,
              onChange: onNameChange,
              onCommit: () => setNameTouched(true),
              ariaLabel: t(`${ns}.nameLabel`),
              datum: 'side.input',
              datumKind: 'input',
            },
          ]}
        />

        <section className="arc-stage-inspector__section" data-container="side > panel#2 > group:children">
          <span className="arc-stage-inspector__section-label" data-datum="side.label#3" data-kind="heading">
            {t(`${ns}.whenHeading`)}
          </span>
          <TypeFieldRowMolecule
            label={t(`${ns}.keyDateLabel`)}
            hint={t(`${ns}.keyDateHint`)}
            layout="stacked"
            mode={editable ? 'edit' : 'disabled'}
            datum="side.key-date"
            controls={[
              {
                kind: 'select',
                value: keyDateValue,
                options: [{ value: '', label: t(`${ns}.keyDateNone`) }, ...keyDateOptions],
                onChange: onKeyDateChange,
                ariaLabel: t(`${ns}.keyDateLabel`),
                datumKind: 'select',
              },
            ]}
          />
          <TypeFieldRowMolecule
            label={t(`${ns}.offsetLabel`)}
            hint={t(`${ns}.offsetHint`)}
            layout="stacked"
            mode={editable && !!keyDateValue ? 'edit' : 'disabled'}
            kind="field"
            datum="side.children"
            labelDatum="side.label"
            errorDatum="side.the-engine-parses"
            errorMessage={
              offsetSupported ? undefined : (
                <Trans
                  i18nKey={`${ns}.offsetInvalid`}
                  values={{ value: offset }}
                  components={{ whole: <strong data-datum="side.whole-days" data-kind="strong" />, b: <strong /> }}
                />
              )
            }
            controls={[
              {
                kind: 'text',
                value: offset,
                placeholder: t(`${ns}.offsetPlaceholder`),
                onChange: onOffsetChange,
                ariaLabel: t(`${ns}.offsetLabel`),
                datum: 'side.e-g-1d',
                datumKind: 'input',
              },
            ]}
          />
          {vacant ? (
            <p className="arc-stage-inspector__note arc-stage-inspector__note--warn" data-datum="side.nothing-has-answered" data-kind="note">
              <GlyphAtom glyph="lucide:help-circle" aria-hidden />
              <Trans
                i18nKey={`${ns}.vacantNote`}
                values={{ need: whenNeed }}
                components={{ need: <strong data-datum="side.when-need" data-kind="strong" /> }}
              />
            </p>
          ) : (
            <p className="arc-stage-inspector__note" data-datum="side.reads-as" data-kind="note">
              <Trans
                i18nKey={`${ns}.readsAsNote`}
                values={{ text: whenText, need: whenNeed }}
                components={{
                  text: <strong data-datum="side.when-text" data-kind="strong" />,
                  need: <strong data-datum="side.when-need#2" data-kind="strong" />,
                }}
              />
            </p>
          )}
        </section>

        <section className="arc-stage-inspector__section" data-container="side > panel#2 > group:children">
          <span className="arc-stage-inspector__section-label" data-datum="side.label#3" data-kind="heading">
            {t(`${ns}.beforeMoveOnHeading`)}
          </span>
          {requirements.length === 0 ? (
            <p className="arc-stage-inspector__note" data-datum="side.nothing-is-required-a-person-can-move-a-" data-kind="note">
              {t(`${ns}.requiresNoneNote`)}
            </p>
          ) : (
            <div className="arc-stage-inspector__requirements" data-container="side > panel#2 > list">
              {requirements.map((requirement, index) => (
                <div
                  key={requirement.id}
                  className="arc-stage-inspector__requirement"
                  data-datum="side.card"
                  data-kind="card"
                  data-container="each(requires)"
                >
                  <span className="arc-stage-inspector__requirement-head">
                    <GlyphAtom glyph="lucide:list-checks" aria-hidden />
                    <strong data-datum="side.requirement" data-kind="strong">
                      {t(`${ns}.requirementLabel`, { index: index + 1 })}
                    </strong>
                    {editable && (
                      <ButtonAtom
                        ref={(el) => {
                          requirementRemoveRefs.current[requirement.id] = el;
                        }}
                        variant="text"
                        shape="icon-square"
                        size="small"
                        icon="pi pi-times"
                        aria-label={t(`${ns}.removeRequirementAria`)}
                        onClick={() => setConfirmingRequirementId(requirement.id)}
                        data-datum="side.remove-requirement"
                        data-kind="button"
                      />
                    )}
                  </span>
                  {editable && confirmingRequirementId === requirement.id && (
                    <InlineConfirmMolecule
                      tone="danger"
                      testId="arc-stage-inspector-requirement-remove-confirm"
                      titleId={`arc-stage-inspector-requirement-remove-confirm-${requirement.id}`}
                      title={t(`${ns}.removeRequirementConfirm.title`, { index: index + 1 })}
                      body={t(`${ns}.removeRequirementConfirm.body`)}
                      actions={[
                        {
                          label: t(`${ns}.removeRequirementConfirm.accept`),
                          onClick: () => {
                            setConfirmingRequirementId(null);
                            onRemoveRequirement(requirement.id);
                          },
                          variant: 'danger',
                          'data-testid': 'arc-stage-inspector-requirement-remove-confirm-accept',
                        },
                        {
                          label: t(`${ns}.removeRequirementConfirm.keep`),
                          onClick: () => keepRequirement(requirement.id),
                          variant: 'text',
                          'data-testid': 'arc-stage-inspector-requirement-remove-confirm-dismiss',
                        },
                      ]}
                    />
                  )}
                  <TypeFieldRowMolecule
                    label={t(`${ns}.whichAspectLabel`)}
                    layout="stacked"
                    mode={editable ? 'edit' : 'disabled'}
                    meta={
                      requirement.aspectRef ? (
                        <TagAtom
                          tone="outline"
                          size="small"
                          label={t(`${ns}.requirementScopeLabel`)}
                          data-datum="side.chip"
                          data-kind="chip"
                        />
                      ) : undefined
                    }
                    controls={[
                      {
                        kind: 'select',
                        value: requirement.aspectRef,
                        options: requirement.aspectOptions,
                        onChange: (value) => onRequirementAspectChange(requirement.id, value),
                        ariaLabel: t(`${ns}.whichAspectLabel`),
                        datum: 'side.select',
                        datumKind: 'select',
                      },
                    ]}
                  />
                  <TypeFieldRowMolecule
                    label={t(`${ns}.promptLabel`)}
                    hint={t(`${ns}.promptHint`)}
                    layout="stacked"
                    mode={editable ? 'edit' : 'disabled'}
                    kind="field"
                    datum="side.children#5"
                    labelDatum="side.label#7"
                    controls={[
                      {
                        kind: 'text',
                        value: requirement.prompt,
                        placeholder: t(`${ns}.promptPlaceholder`),
                        onChange: (value) => onRequirementPromptChange(requirement.id, value),
                        ariaLabel: t(`${ns}.promptLabel`),
                        datum: 'side.e-g-record-what-caused-this',
                        datumKind: 'input',
                      },
                    ]}
                  />
                </div>
              ))}
            </div>
          )}
          {editable && (
            <ButtonAtom
              variant="text"
              size="small"
              icon={<GlyphAtom glyph="lucide:plus" aria-hidden />}
              onClick={onAddRequirement}
              data-datum="side.require-an-aspect"
              data-kind="button"
            >
              {t(`${ns}.requireAspect`)}
            </ButtonAtom>
          )}
          <p className="arc-stage-inspector__note" data-datum="side.only-aspects" data-kind="note">
            {t(`${ns}.onlyAspectsNote`, { name: pinName })}
          </p>
          <p className="arc-stage-inspector__note" data-datum="side.an-unmet-requirement-blocks-a-manual-exi" data-kind="note">
            {t(`${ns}.unmetRequirementNote`)}
          </p>
        </section>

        <section className="arc-stage-inspector__section" data-container="side > panel#2 > group:children">
          <span className="arc-stage-inspector__section-label" data-datum="side.label#3" data-kind="heading">
            {t(`${ns}.restsHeading`)}
          </span>
          {terminalAllowed ? (
            <ChoiceCardGroupAtom
              ariaLabel={t(`${ns}.restsHeading`)}
              value={isTerminal ? 'terminal' : 'continues'}
              onChange={(value) => onIsTerminalChange(value === 'terminal')}
              disabled={!editable}
              options={[
                {
                  value: 'continues',
                  label: t(`${ns}.continuesLabel`),
                  note: t(`${ns}.continuesNote`),
                  icon: 'lucide:arrow-right',
                  datum: 'side.button',
                  datumKind: 'button',
                },
                {
                  value: 'terminal',
                  label: t(`${ns}.finalLabel`),
                  note: t(`${ns}.finalNote`),
                  icon: 'lucide:flag',
                  datum: 'side.button',
                  datumKind: 'button',
                },
              ]}
            />
          ) : (
            <p className="arc-stage-inspector__note arc-stage-inspector__note--warn" data-datum="side.arcs-have-no-final-stage" data-kind="note">
              <GlyphAtom glyph="lucide:infinity" aria-hidden />
              {t(`${ns}.noTerminalNote`, { archetype: archetypeLabel, note: archetypeNote })}
            </p>
          )}
        </section>

        <section className="arc-stage-inspector__section" data-container="side > panel#2 > group:children">
          <span className="arc-stage-inspector__section-label" data-datum="side.label#3" data-kind="heading">
            {t(`${ns}.ownsHeading`)}
          </span>
          <TypeFieldRowMolecule
            label={t(`${ns}.assignedToLabel`)}
            layout="stacked"
            mode={editable ? 'edit' : 'disabled'}
            datum="side.assigned-to"
            controls={[
              {
                kind: 'select',
                value: assignedTo,
                options: assignedToOptions,
                onChange: onAssignedToChange,
                ariaLabel: t(`${ns}.assignedToLabel`),
                datumKind: 'select',
              },
            ]}
          />
        </section>

        <section className="arc-stage-inspector__section" data-container="side > panel#2 > group:children">
          <span className="arc-stage-inspector__section-label" data-datum="side.label#3" data-kind="heading">
            {t(`${ns}.intentsHeading`)}
          </span>
          {intents.length === 0 ? (
            <p className="arc-stage-inspector__note" data-datum="side.no-intents-yet-a-stage-with-no-intents-s" data-kind="note">
              {t(`${ns}.noIntentsNote`)}
            </p>
          ) : (
            <div className="arc-stage-inspector__intents">
              {intents.map((intent) => (
                <span key={intent.id} className="arc-stage-inspector__intent-row">
                  <GlyphAtom glyph="lucide:circle" aria-hidden />
                  {intent.name}
                  <i>{intent.perspectiveLabel}</i>
                </span>
              ))}
            </div>
          )}
        </section>
      </div>
      {editable && confirmingRemove && (
        <InlineConfirmMolecule
          tone="danger"
          testId="arc-stage-inspector-remove-confirm"
          titleId="arc-stage-inspector-remove-confirm-title"
          title={t(`${ns}.removeConfirm.title`, { name })}
          body={
            <>
              {t(`${ns}.removeConfirm.body`, {
                intents: t(`${ns}.removeConfirm.intentCount`, { count: intents.length }),
                actions: t(`${ns}.removeConfirm.actionCount`, { count: actionCount }),
              })}
              {inboundTransitionCount > 0 && ` ${t(`${ns}.removeConfirm.dangling`, { count: inboundTransitionCount })}`}
              {` ${t(`${ns}.removeConfirm.reversible`)}`}
            </>
          }
          actions={[
            {
              label: t(`${ns}.removeConfirm.accept`),
              onClick: onRemove,
              variant: 'danger',
              'data-testid': 'arc-stage-inspector-remove-confirm-accept',
            },
            {
              label: t(`${ns}.removeConfirm.keep`),
              onClick: keepStage,
              variant: 'text',
              'data-testid': 'arc-stage-inspector-remove-confirm-dismiss',
            },
          ]}
        />
      )}
      {editable && (
        <div className="arc-stage-inspector__actions" data-container="side > actions">
          <ButtonAtom variant="secondary" size="small" onClick={onApply} data-datum="side.apply" data-kind="button">
            {t(`${ns}.apply`)}
          </ButtonAtom>
          <ButtonAtom
            ref={removeButtonRef}
            variant="danger"
            size="small"
            icon={<GlyphAtom glyph="lucide:trash-2" aria-hidden />}
            onClick={() => setConfirmingRemove(true)}
            data-datum="side.remove-stage"
            data-kind="button"
          >
            {t(`${ns}.removeStage`)}
          </ButtonAtom>
        </div>
      )}
    </div>
  );
};

ArcStageInspectorMolecule.displayName = 'ArcStageInspectorMolecule';

export default ArcStageInspectorMolecule;
